import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { ColorHelpers } from '@shared/helpers/color-helpers';

export function AmCharts4Theme(target: any) {
  const text = '#ffffff';
  const altText = '#000000';
  const button = '#4d4355';
  const grid = '#ffffff';
  const thumb = '#8805DF';
  const scrollbar = '#dcb9f8';
  const background = '#17111b';

  // no data indicator
  if (target instanceof am4core.Container && (target as any).customClass === 'no-data-container') {
    target.background.fill = am4core.color(background);
  }

  // Label text color
  if (target instanceof am4core.Label) {
    target.fill = am4core.color(text);
  }

  // Tooltips
  if (
    target instanceof am4charts.LineSeries ||
    target instanceof am4charts.ColumnSeries ||
    target instanceof am4charts.StepLineSeries ||
    target instanceof am4charts.XYSeries ||
    target instanceof am4charts.Series
  ) {
    target.adapter.add('tooltipHTML', (html, series) => {
      if (!html || typeof html !== 'string') return html;

      const bgFill = series.tooltip?.background?.fill;
      let textColour = '#ffffff'; // fallback

      if (bgFill instanceof am4core.Color) {
        textColour = ColorHelpers.computeTextColor(bgFill.hex);
      }

      // Inject the color into the wrapper div
      return `<div class="chart-tooltip" style="color: ${textColour};">${html}</div>`;
    });
    // target.events.on('inited', () => {
    //   setTimeout(() => {
    //     if (target.tooltip && target.tooltip.label && target.tooltip.background) {
    //       const bgFill = target.tooltip.background.fill;
    //       console.log('bgFill', (bgFill as am4core.Color).hex);
    //       // let textColour = bgFill instanceof am4core.Color ? ColorHelpers.computeTextColor(bgFill.hex) : text;
    //       // console.log('textColour', textColour);
    //       const textColour = '#0f00f0';
    //       target.tooltip.label.fill = am4core.color(textColour);
    //     }
    //   }, 10);
    // });
    // )
    // target.tooltip.label.events.once('ready', () => {
    //   const bgFill = target.background.fill;
    //   const textColour = bgFill instanceof am4core.Color ? ColorHelpers.computeTextColor(bgFill.hex) : text;
    //   console.log('textColor', textColour);
    //   target.tooltip.label.fill = am4core.color(textColour);
    //
    //   // ['defaultState', 'hoverState', 'downState'].forEach(stateName => {
    //   //   const state = (target.label.states as any).getKey(stateName);
    //   //   if (state && state.properties) {
    //   //     state.properties.fill = am4core.color(textColour);
    //   //   }
    //   // });
    // });
  }

  // Axis labels & grid lines
  if (target instanceof am4charts.AxisRenderer) {
    target.labels.template.fill = am4core.color(text);
    target.grid.template.stroke = am4core.color(grid);
  }

  // Base lines that are specifically at 0
  if (target instanceof am4charts.AxisRendererY || target instanceof am4charts.AxisRendererX) {
    if (target.baseGrid) {
      target.baseGrid.stroke = am4core.color(grid);
      target.baseGrid.strokeOpacity = 0.15;
    }
  }

  if (target instanceof am4charts.AxisLabel) {
    target.fill = am4core.color(text);
  }

  // Scrollbar
  if (target instanceof am4core.Scrollbar) {
    target.background.fill = am4core.color(scrollbar);
    target.background.fillOpacity = 1;
    target.thumb.background.fill = am4core.color(thumb);
    target.thumb.background.fillOpacity = 0.6;
    target.thumb.background.strokeOpacity = 0;

    target.thumb.background.states.getKey('hover').properties.fill = am4core.color(thumb);
    target.thumb.background.states.getKey('down').properties.fill = am4core.color(thumb);
    target.thumb.background.states.getKey('hover').properties.fillOpacity = 0.5;
    target.thumb.background.states.getKey('down').properties.fillOpacity = 0.5;
  }

  if (target instanceof am4charts.XYChartScrollbar) {
    // AMCharts takes a moment to get the series ready
    setTimeout(() => {
      // Style the scrollbar itself
      target.background.fill = am4core.color(scrollbar);
      target.thumb.background.fill = am4core.color(thumb);
      target.thumb.background.fillOpacity = 0.6;

      // Style the unselected area
      if (target.unselectedOverlay) {
        target.unselectedOverlay.fill = am4core.color(scrollbar);
        target.unselectedOverlay.fillOpacity = 0.3;
      }

      // Style the series in the scrollbar if it exists
      if (target.scrollbarChart.series.getIndex(0)) {
        target.scrollbarChart.series.getIndex(0).fill = am4core.color(scrollbar);

        // Make sure axis labels are visible and styled correctly
        target.scrollbarChart.xAxes.getIndex(0).renderer.labels.template.toFront();
        target.scrollbarChart.xAxes.getIndex(0).renderer.labels.template.fillOpacity = 1;
        target.scrollbarChart.xAxes.getIndex(0).renderer.labels.template.fill = am4core.color(altText);
      }
    }, 0);
  }

  // Buttons
  if (target instanceof am4core.Button) {
    target.background.fill = am4core.color(button);
    ['hover', 'down', 'active'].forEach(stateName => {
      const state = target.background.states.getKey(stateName);
      if (state) {
        state.properties.fill = am4core.color(button);
        state.properties.fillOpacity = 1;
      }
    });
  }

  if (target instanceof am4charts.PieChart) {
    target.background.fill = am4core.color(background);
  }
  if (target instanceof am4charts.PieSeries) {
    target.ticks.template.stroke = am4core.color(text);
  }
  if (target instanceof am4charts.LabelBullet) {
    if (target.label) {
      target.label.fill = am4core.color(text);
    }
  }

  // Series colours
  if (target instanceof am4core.ColorSet) {
    target.list = [
      am4core.color('#008071'),
      am4core.color('#4E00C2'),
      am4core.color('#DB0AAE'),
      am4core.color('#9500B3'),
      am4core.color('#558104'),
      am4core.color('#104AF9'),
      am4core.color('#6600FF'),
      am4core.color('#008A27'),
      am4core.color('#008299'),
      am4core.color('#8805DF'),
      am4core.color('#E21D1D'),
      am4core.color('#C825CB'),
      am4core.color('#7A52FF'),
      am4core.color('#3E6CF4'),
      am4core.color('#DC3A04'),
      am4core.color('#E22265')
    ];
  }
}
